export const RESEARCH_REPO = {
  canonicalPath: '/research/municipal-landfill-methane-responsibility',
  canonicalUrl: 'https://climateplans.org/research/municipal-landfill-methane-responsibility/',
  aliasPath: '/solid-waste-paper',
  shortTitle: 'Routing-Resolved Municipal Landfill Methane Responsibility',
  title:
    'Routing-Resolved Municipal Landfill Methane Responsibility under Alternative Accounting Conventions',
  subtitle: '',
  authors: ['Christopher M. Jones'],
  correspondingEmail: 'cmjones@berkeley.edu',
  orcid: '0000-0002-1813-7326',
  orcidUrl: 'https://orcid.org/0000-0002-1813-7326',
  affiliation:
    'CoolClimate Network, California Institute for Energy and Environment, University of California, Berkeley, Berkeley, California, USA',
  journal: 'Journal of Industrial Ecology',
  articleType: 'Methodology Article',
  editorialDepartment: 'Methods, Tools and Software',
  subjectArea:
    'Advances in industrial ecology method — material and substance flow analysis',
  statusBadge: 'Submitted to the Journal of Industrial Ecology on July 14, 2026',
  editorialStatus: 'Submitted',
  submissionDate: '2026-07-14',
  submissionDateDisplay: 'July 14, 2026',
  submissionPackageSha256: '0b3a8126e7f2bf843cd5192d353cfd88955415cb8af4c5a5d8cdc8d9df43c647',
  year: 2026,
  release: 'v1.0.0',
  releaseLabel: 'Archived research release',
  releaseDate: '2026-07-14',
  releaseDateDisplay: 'July 14, 2026',
  lastUpdated: '2026-07-14',
  doi: '10.5281/zenodo.21363462',
  doiUrl: 'https://doi.org/10.5281/zenodo.21363462',
  zenodoUrl: 'https://zenodo.org/records/21363462',
  zenodoDownloadUrl:
    'https://zenodo.org/api/records/21363462/files/SW01_v1.0.0_release_archive.zip/content',
  archiveFilename: 'SW01_v1.0.0_release_archive.zip',
  archiveSha256: '45450e30d3ef4e3941fc75c0ace8a1c937f8c39c9dc0a7335920965130241e47',
  funding:
    'This work was supported by the University of California California Climate Action Seed Grant R02CP7580, “Climate Action Planning Tools: Empowering Equitable Transitions for California Communities.”',
} as const;

export const STUDY_OVERVIEW =
  'Municipal greenhouse-gas inventories assign landfill responsibility to the jurisdiction generating waste even when disposal and methane emissions occur elsewhere. This study links California jurisdiction-to-destination waste flows with compatible facility-year landfill methane estimates and evaluates how receiving-landfill identity, routing period, temporal accounting convention, and methane-recovery assumptions affect municipal responsibility.';

export const IE_FRAMING =
  'Current-flow allocation is treated as a contemporaneous-flow attribution method: an annual facility burden is distributed among current waste suppliers according to observed route shares. This is analogous to attributional accounting in environmentally extended input-output, capital-service, Waste Input-Output, and life-cycle assessment practice. Prospective commitment and historical cohort attribution remain distinct estimands.';

export const STUDY_SUMMARY = `${STUDY_OVERVIEW} ${IE_FRAMING}`;

export const ANALYTICAL_UNIVERSE = [
  { label: 'U0 routed place-level network (2019)', value: '30,750,592 short tons' },
  { label: 'U0 jurisdictions', value: '356' },
  { label: 'U0 routing edges', value: '3,450' },
  { label: 'U0 share of reported California 2019 landfill disposal', value: '72.87%' },
  { label: 'U1 facility-specific matched subset', value: '29,117,045 short tons' },
  { label: 'U1 jurisdictions', value: '355' },
  { label: 'U1 facility-specific edges', value: '3,029' },
  { label: 'U1 within-network match (U1/U0, not statewide coverage)', value: '94.69%' },
  { label: 'U1 share of statewide landfill-disposal reference', value: '69.00%' },
  { label: 'U1 edges passing authoritative final-landfill verification', value: '100%' },
] as const;

export const PRIMARY_RESULTS = [
  { label: 'Eligible comparison jurisdictions', value: '337' },
  { label: 'Median divergence from matched statewide factor', value: '28.46%' },
  { label: 'Median divergence from county factors', value: '15.52%' },
  { label: 'Spearman rank correlation', value: '0.896' },
  { label: 'Jurisdictions shifting more than 25 positions', value: '138' },
  { label: 'Median route-window effect', value: '22.88%' },
  { label: 'Allocated annual facility burden', value: '4,053,451 MT CO₂e' },
] as const;

export const TEMPORAL_COMPARISONS = [
  { label: '75% recovery sensitivity', value: '2.02 commitment/current-flow ratio' },
  { label: 'ICLEI-style factor benchmark', value: '2.19' },
  { label: '35% recovery', value: '5.25' },
  { label: 'No recovery', value: '8.07' },
  { label: 'Alternative DOCf', value: '6.73' },
] as const;

export const TEMPORAL_INTERPRETATION =
  'The five- to eightfold comparisons represent low- and no-recovery sensitivity bounds. A recurring 75% protocol-practice convention produces comparisons closer to twofold. The 75% value is an accounting convention rather than a measured statewide physical recovery rate.';

export const SCOPE_NOTES = [
  'The routed network is not a statewide census of all disposal.',
  'Route missingness is systematic and documented in the release.',
  'Results apply to routed and facility-matched jurisdictions in the analytical universe.',
  'Current-flow allocation assigns period responsibility and does not reconstruct the historical waste cohorts producing annual methane.',
  'The facility allocation intensity is not a landfill-operator performance score.',
  'The canonical facility-factor table is a checksum-controlled frozen derived input because its original builder was not recovered.',
  'The analysis reproduces from archived local inputs and scripts without Supabase.',
  'A 75% methane-recovery assumption reduces modeled methane after disposal; it does not remove 75% of waste tonnage.',
  'SB 1383 organics diversion changes future composition, so fixed-composition commitment cases are sensitivities rather than forecasts.',
] as const;

export const CONTRIBUTIONS = [
  {
    title: 'Observed routing network',
    text: 'Statewide jurisdiction-to-destination waste-flow network with explicit facility classification.',
  },
  {
    title: 'Compatible facility-year matching',
    text: 'Facility-specific methane and receipt matching with explicit unmatched-flow and fallback accounting.',
  },
  {
    title: 'Matched-tonnage comparators',
    text: 'Statewide- and county-factor estimates applied to identical covered tonnage.',
  },
  {
    title: 'Separate temporal indicators',
    text: 'Annual current-flow allocation and prospective commitment reported as non-additive indicators.',
  },
] as const;

export const REPRO_STATUS = [
  ['Primary route-resolved allocation', 'Yes', 'v1.0.0 release archive'],
  ['Statewide comparator', 'Yes', 'v1.0.0 release archive'],
  ['County comparator', 'Yes', 'v1.0.0 release archive'],
  ['Coverage and fallback tests', 'Yes', 'Archived analytical outputs in v1.0.0 release'],
  ['Route-window pilot', 'Yes', 'Archived routing sensitivity outputs in v1.0.0 release'],
  ['Commitment pilot', 'Yes', 'Archived commitment scenario outputs in v1.0.0 release'],
  ['Main figures', 'Yes', 'Canonical pipeline figures in v1.0.0 release'],
  ['Supplementary figures (S1–S3)', 'Yes', 'Canonical pipeline figures in v1.0.0 release'],
] as const;

export const RELEASE_CONTENTS = [
  'Figure data and supporting-table data',
  'Analysis scripts',
  'Claim-to-artifact ledger',
  'Environment requirements',
  'Provenance manifests',
  'CITATION.cff and checksums',
  'Controlled-derived-input manifest',
] as const;

export const LICENSE_STATEMENT =
  'ClimatePlans-created derived datasets and documentation are licensed under Creative Commons Attribution 4.0 International. Analysis scripts are licensed under the MIT License. Original CalRecycle and EPA records are not relicensed and remain subject to the originating agencies’ terms.';

export const PROVENANCE_STATEMENT =
  'The canonical facility-factor table is retained as a checksum-controlled frozen derived input because its original historical builder was not recovered. This limitation is documented in the release rather than represented as complete historical raw-to-output reproducibility.';

export const CITATION_PLAIN =
  'Jones, Christopher M. (2026). Data and Code for “Routing-Resolved Municipal Landfill Methane Responsibility under Alternative Accounting Conventions” (Version 1.0.0). Zenodo. https://doi.org/10.5281/zenodo.21363462';

export const CITATION_REPO = CITATION_PLAIN;

export const PUBLIC_AGENCIES = [
  { name: 'CalRecycle Disposal Reporting System', url: 'https://calrecycle.ca.gov/' },
  { name: 'U.S. EPA Greenhouse Gas Reporting Program', url: 'https://www.epa.gov/ghgreporting' },
] as const;

export type ManifestFile = {
  title: string;
  filename: string;
  type: string;
  description?: string;
  size_bytes?: number;
  sha256?: string;
  version?: string;
  status: string;
  license?: string;
  source?: string;
  download_path?: string;
};

export type ManifestDoc = {
  release: string;
  release_label: string;
  generated: string;
  doi?: string;
  doi_url?: string;
  zenodo_url?: string;
  archive_sha256?: string;
  files: ManifestFile[];
  pending?: { title: string; type: string; status: string }[];
};
